import { Box, LinearProgress } from '@mui/material';
import { ComponentType, ReactNode, RefObject } from 'react';
import {
  GroupedVirtuoso,
  GroupedVirtuosoHandle,
  ListRange,
  ScrollSeekConfiguration,
  ScrollSeekPlaceholderProps,
  StateSnapshot,
  TopItemListProps,
} from 'react-virtuoso';
import { CatalogProductsTestIds } from '../../../../utils/testIds/CatalogProducts.testIds';
import { CatalogAdjacentRows, CatalogProductsListV2Result } from './catalogProductsListV2.types';
import CatalogListError from './components/CatalogListError';
import CatalogProductRowV2 from './components/CatalogProductRowV2';
import CatalogProductsDragAndDropBoundary from './components/CatalogProductsDragAndDropBoundary/CatalogProductsDragAndDropBoundary';
import { CatalogScrollSeekContext } from './components/CatalogScrollSeekPlaceholder';
import { CatalogProductsDragAndDrop } from './hooks/useCatalogProductsDragAndDrop.types';

interface ProductCollectionListV2Props {
  catalogId: string;
  list: CatalogProductsListV2Result;
  emptyState: ReactNode;
  groupContent: (groupIndex: number) => ReactNode;
  virtuoso?: RefObject<GroupedVirtuosoHandle>;
  scrollParent?: HTMLElement | null;
  restoreStateFrom?: StateSnapshot;
  dragAndDrop?: CatalogProductsDragAndDrop;
  scrollSeekConfiguration?: ScrollSeekConfiguration;
  scrollSeekPlaceholder?: ComponentType<
    ScrollSeekPlaceholderProps & { context: CatalogScrollSeekContext }
  >;
  maxItemsPerRow?: number;
  onRangeChanged?: (range: ListRange) => void;
  onIsScrolling?: (isScrolling: boolean) => void;
}

const EMPTY_STATE_MIN_HEIGHT = 480;
const StaticTopItemList = (props: TopItemListProps) => (
  <div {...props} style={{ position: 'static' }} />
);

const ListFooter = () => <Box sx={{ pb: 2 }} />;

const resolveAdjacentRows = (
  list: Extract<CatalogProductsListV2Result, { status: 'ready' }>,
  rowIndex: number,
): CatalogAdjacentRows => {
  const lastRowIndex = list.groupCounts.reduce((total, count) => total + count, 0) - 1;

  return {
    previous: rowIndex > 0 ? list.resolveRow(rowIndex - 1) : undefined,
    next: rowIndex < lastRowIndex ? list.resolveRow(rowIndex + 1) : undefined,
  };
};

const getIsEmpty = (list: CatalogProductsListV2Result) => {
  if (list.status !== 'ready') {
    return false;
  }

  return list.sections.length === 0 || list.groupCounts.every((count) => count === 0);
};

const ProductCollectionListV2 = ({
  catalogId,
  list,
  emptyState,
  groupContent,
  virtuoso,
  scrollParent,
  restoreStateFrom,
  dragAndDrop,
  scrollSeekConfiguration,
  scrollSeekPlaceholder,
  maxItemsPerRow = 1,
  onRangeChanged,
  onIsScrolling,
}: ProductCollectionListV2Props) => {
  if (list.status === 'list-error') {
    return <CatalogListError onRetry={list.retry} />;
  }

  const isEmpty = getIsEmpty(list);

  const rows = list.status === 'ready' && (
    <GroupedVirtuoso
      ref={virtuoso}
      customScrollParent={scrollParent ?? undefined}
      groupCounts={list.groupCounts}
      restoreStateFrom={restoreStateFrom}
      rangeChanged={onRangeChanged}
      isScrolling={onIsScrolling}
      scrollSeekConfiguration={scrollSeekConfiguration}
      context={{ maxItemsPerRow }}
      components={{
        TopItemList: StaticTopItemList,
        Footer: ListFooter,
        ScrollSeekPlaceholder: scrollSeekPlaceholder,
      }}
      groupContent={groupContent}
      itemContent={(rowIndex) => (
        <CatalogProductRowV2
          catalogId={catalogId}
          rowIndex={rowIndex}
          row={list.resolveRow(rowIndex)}
          adjacentRows={resolveAdjacentRows(list, rowIndex)}
          drag={
            dragAndDrop?.isEnabled
              ? {
                  activeId: dragAndDrop.activeItem?.id ?? null,
                  isDraggingDisabled: dragAndDrop.isDraggingDisabled,
                  dropIndicator: dragAndDrop.dropIndicator,
                }
              : undefined
          }
        />
      )}
    />
  );

  return (
    <Box test-id={CatalogProductsTestIds.LIST_V2}>
      {list.status === 'loading' && <LinearProgress />}

      {isEmpty && (
        <Box
          test-id={CatalogProductsTestIds.EMPTY_STATE}
          sx={{ display: 'flex', minHeight: EMPTY_STATE_MIN_HEIGHT }}
        >
          {emptyState}
        </Box>
      )}

      {list.status === 'ready' && !isEmpty && (
        <>
          {dragAndDrop ? (
            <CatalogProductsDragAndDropBoundary dragAndDrop={dragAndDrop}>
              {rows}
            </CatalogProductsDragAndDropBoundary>
          ) : (
            rows
          )}
        </>
      )}
    </Box>
  );
};

export default ProductCollectionListV2;
